import { c as _c } from "react/compiler-runtime";
import type { SVGProps } from "react";

/**
 * Layout-switcher + header glyphs ported from Notion's AI chat for visual
 * parity. Filled (not stroked) so they read at small sizes; size via a
 * `size-*` className like the lucide icons used elsewhere.
 */
type IconProps = {
  className?: string;
} & SVGProps<SVGSVGElement>;

/** Docked sidebar — a window with a tall pane on the trailing edge. */
export function PeekSideIcon(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M10.392 6.125a.5.5 0 0 0-.5.5v6.75a.5.5 0 0 0 .5.5h4.683a.5.5 0 0 0 .5-.5v-6.75a.5.5 0 0 0-.5-.5z" />;
    t2 = <path d="M4.5 4.125A2.125 2.125 0 0 0 2.375 6.25v7.5c0 1.174.951 2.125 2.125 2.125h11a2.125 2.125 0 0 0 2.125-2.125v-7.5A2.125 2.125 0 0 0 15.5 4.125zM3.625 6.25c0-.483.392-.875.875-.875h11c.483 0 .875.392.875.875v7.5a.875.875 0 0 1-.875.875h-11a.875.875 0 0 1-.875-.875z" />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className || $[6] !== props) {
    t3 = <svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 20 20" {...props}>{t1}{t2}</svg>;
    $[5] = className;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/** Floating — a window with a small pane in the trailing-bottom corner. */
export function PeekCornerIcon(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M11.93 9.125a.5.5 0 0 0-.5.5v3.75a.5.5 0 0 0 .5.5h3.145a.5.5 0 0 0 .5-.5v-3.75a.5.5 0 0 0-.5-.5z" />;
    t2 = <path d="M4.5 4.125A2.125 2.125 0 0 0 2.375 6.25v7.5c0 1.174.951 2.125 2.125 2.125h11a2.125 2.125 0 0 0 2.125-2.125v-7.5A2.125 2.125 0 0 0 15.5 4.125zM3.625 6.25c0-.483.392-.875.875-.875h11c.483 0 .875.392.875.875v7.5a.875.875 0 0 1-.875.875h-11a.875.875 0 0 1-.875-.875z" />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className || $[6] !== props) {
    t3 = <svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 20 20" {...props}>{t1}{t2}</svg>;
    $[5] = className;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/** Full screen — a window with a single wide pane filling it. */
export function PeekFullIcon(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M4.93 6.125a.5.5 0 0 0-.5.5v6.75a.5.5 0 0 0 .5.5h10.145a.5.5 0 0 0 .5-.5v-6.75a.5.5 0 0 0-.5-.5z" />;
    t2 = <path d="M4.5 4.125A2.125 2.125 0 0 0 2.375 6.25v7.5c0 1.174.951 2.125 2.125 2.125h11a2.125 2.125 0 0 0 2.125-2.125v-7.5A2.125 2.125 0 0 0 15.5 4.125zM3.625 6.25c0-.483.392-.875.875-.875h11c.483 0 .875.392.875.875v7.5a.875.875 0 0 1-.875.875h-11a.875.875 0 0 1-.875-.875z" />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className || $[6] !== props) {
    t3 = <svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 20 20" {...props}>{t1}{t2}</svg>;
    $[5] = className;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/** New chat — a speech bubble with a plus. */
export function NewChatIcon(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M16.938 9.353c0-2.97-2.539-5.54-6.545-5.697L10 3.648c-4.232 0-6.938 2.638-6.938 5.705 0 1.438.583 2.752 1.617 3.759a.63.63 0 0 1 .18.547 7.3 7.3 0 0 1-.89 2.528c1.108-.13 2.12-.614 3.01-1.344l.063-.044a.63.63 0 0 1 .505-.073 9 9 0 0 0 2.454.333l.392-.008c4.006-.158 6.545-2.727 6.545-5.698m1.25 0c0 3.803-3.234 6.766-7.747 6.947l-.44.01c-.88 0-1.712-.107-2.485-.3-1.349 1.022-2.985 1.62-4.826 1.428a.625.625 0 0 1-.406-1.033c.712-.818 1.096-1.737 1.284-2.642-1.116-1.197-1.756-2.734-1.756-4.41 0-3.926 3.447-6.955 8.189-6.955l.44.009c4.512.181 7.747 3.143 7.747 6.946" />;
    t2 = <path d="M10 5.875c.346 0 .626.28.626.625v2.375H13a.625.625 0 1 1 0 1.25h-2.375V12.5a.625.625 0 1 1-1.25 0v-2.375H7a.625.625 0 1 1 0-1.25h2.375V6.5c0-.346.28-.625.626-.625" />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className || $[6] !== props) {
    t3 = <svg aria-hidden="true" className={className} fill="currentColor" viewBox="0 0 20 20" {...props}>{t1}{t2}</svg>;
    $[5] = className;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}